/*                               */
import * as tokens from "@otto-ec/design-tokens/build-private/js/tokens";
import { findClippingElement, findViewportElement } from "../../utils/findContainingElement";
import { PopoverPosition, type PopoverPositioningResult } from "./Popover.types";

const DEFAULT_CLIPPING_DISTANCE = tokens.components.toggletip.frameSpacing();
const DEFAULT_MAX_WIDTH = tokens.components.toggletip.maxWidth(); /*  */
const ARROW_SIZE = tokens.components.toggletip.arrowHeight(); /**/
const DEBUG = false; /*                                       */

export function defer(): {
  promise: Promise<unknown>;
  resolve: (value: unknown) => void;
  pending: boolean;
} {
  let resolve: (value: unknown) => void = () => {};
  let pending = true;
  const promise = new Promise((res) => {
    resolve = (value: unknown) => {
      if (!pending) return;
      pending = false;
      res(value);
    };
  });
  return { promise, resolve, pending };
}

const getBorderBoxCorrectionValues = (
  element: HTMLElement,
): {
  left: number;
  top: number;
  right: number;
  bottom: number;
} => {
  const style = window.getComputedStyle(element);
  const borderLeft = parseFloat(style.borderLeftWidth);
  const borderTop = parseFloat(style.borderTopWidth);
  const borderRight = parseFloat(style.borderRightWidth);
  const borderBottom = parseFloat(style.borderBottomWidth);

  return {
    left: borderLeft,
    top: borderTop,
    right: borderRight,
    bottom: borderBottom,
  };
};

const getBorderCorrectedBoundingClientRect = (element: HTMLElement): DOMRectReadOnly => {
  const bounding = element.getBoundingClientRect();
  const paddingCorrection = getBorderBoxCorrectionValues(element);
  const x = bounding.x + paddingCorrection.left;
  const y = bounding.y + paddingCorrection.top;
  const width = bounding.width - paddingCorrection.left - paddingCorrection.right;
  const height = bounding.height - paddingCorrection.top - paddingCorrection.bottom;
  return new DOMRectReadOnly(x, y, width, height);
};

const getClippingBoundingClientRect = (element: HTMLElement): DOMRectReadOnly => {
  const clippingElement = findClippingElement(element);
  const bounding = clippingElement?.getBoundingClientRect();
  const x = bounding ? bounding.x : 0;
  const y = bounding ? bounding.y : 0;
  const width = bounding ? bounding.width : window.innerWidth;
  const height = bounding ? bounding.height : window.innerHeight;
  return new DOMRectReadOnly(x, y, width, height);
};

const getViewportBoundingClientRect = (element: HTMLElement, adjustForScroll: boolean): DOMRect => {
  /*                                                                                                            */
  const viewport = findViewportElement(element);
  if (viewport) {
    const bounding = viewport.getBoundingClientRect();
    /*                                                                   */
    /*                                                                         */
    /*                                               */
    const x = bounding.x - (adjustForScroll ? viewport.scrollLeft : 0);
    const y = bounding.y - (adjustForScroll ? viewport.scrollTop : 0);
    const width = bounding.width;
    const height = bounding.height;
    if (DEBUG) {
      viewport.style.outline = "5px solid rgba(180,80,5,0.2)"; /*                                    */
    }
    return new DOMRectReadOnly(x, y, width, height) as DOMRect;
  }
  /*                                                                                                                  */
  return new DOMRectReadOnly(0, 0, window.innerWidth, window.innerHeight) as DOMRect;
};

export const calculatePopoverPosition = (
  popoverElement: HTMLElement,
  contextElement: HTMLElement,
  preferredPosition: PopoverPosition = PopoverPosition.BOTTOM,
  alwaysInViewport: boolean,
  useFixedPositioning: boolean = true,
  usePopoverApi: boolean = true,
): PopoverPositioningResult => {
  const popoverRect = popoverElement.getBoundingClientRect();
  const contextRect = contextElement.getBoundingClientRect();
  /*                                                                                           */
  const containingRect = useFixedPositioning
    ? getViewportBoundingClientRect(
        usePopoverApi ? document.body : (popoverElement.parentElement as HTMLElement),
        true,
      )
    : getBorderCorrectedBoundingClientRect(popoverElement.parentElement as HTMLElement);
  const clippingRect = useFixedPositioning
    ? getViewportBoundingClientRect(
        usePopoverApi ? document.body : (popoverElement.parentElement as HTMLElement),
        false,
      )
    : getClippingBoundingClientRect(popoverElement.parentElement as HTMLElement);

  const inheritStyles = getComputedStyle(popoverElement);
  const clippingDistance = parseInt(
    inheritStyles.getPropertyValue("--popover-frame-spacing") || String(DEFAULT_CLIPPING_DISTANCE),
  );

  /*                      */
  let finalPosition: PopoverPosition = preferredPosition;
  let showAnchor: boolean = true;
  let left: number | undefined = undefined;
  let right: number | undefined = undefined;
  let top: number | undefined = undefined;
  let bottom: number | undefined = undefined;

  /*                                                            */
  const popoverHeight = popoverRect.height;

  /*                                                                                           */
  /*                                                                       */
  const clippedSpaceAbove = contextRect.top - clippingRect.top;
  const clippedSpaceBelow = clippingRect.bottom - contextRect.bottom;
  const shouldSwitchPosition =
    preferredPosition == PopoverPosition.BOTTOM
      ? clippedSpaceBelow < popoverHeight && popoverHeight < clippedSpaceAbove
      : clippedSpaceAbove < popoverHeight;
  if (shouldSwitchPosition) {
    finalPosition =
      preferredPosition === PopoverPosition.BOTTOM ? PopoverPosition.TOP : PopoverPosition.BOTTOM;
  }

  /*                                                                  */
  if (finalPosition === PopoverPosition.BOTTOM) {
    top = contextRect.bottom;
  } else {
    bottom = contextRect.top;
  }

  if (alwaysInViewport && top !== undefined) {
    if (top + popoverHeight > clippingRect.bottom) {
      top = clippingRect.bottom - popoverHeight;
      showAnchor = false;
    }
    if (top < clippingRect.top) {
      top = clippingRect.top;
    }
  }
  if (alwaysInViewport && bottom !== undefined) {
    if (bottom - popoverHeight < clippingRect.top) {
      bottom = clippingRect.top + popoverHeight;
      showAnchor = false;
    }
    if (bottom > clippingRect.bottom) {
      bottom = clippingRect.bottom;
    }
  }

  /*                                    */
  left = contextRect.width / 2 + contextRect.left - popoverRect.width / 2;
  /*                                       */
  if (
    left < clippingRect.left + clippingDistance ||
    popoverRect.width > clippingRect.width - clippingDistance * 2
  ) {
    left = clippingRect.left + clippingDistance;
  } else if (left + popoverRect.width > clippingRect.right - clippingDistance) {
    left = undefined;
    right = clippingRect.right - clippingDistance;
  }

  /*                                                                 */
  let anchorLeftPosition: number = contextRect.width / 2 + contextRect.left;
  if (left !== undefined) anchorLeftPosition -= left;
  if (right !== undefined) anchorLeftPosition -= right - popoverRect.width;

  /*                                      */
  if (left !== undefined) left -= containingRect.left;
  if (top !== undefined) top -= containingRect.top;
  if (bottom !== undefined) bottom = containingRect.bottom - bottom;
  if (right !== undefined) right = containingRect.right - right;
  if (anchorLeftPosition < ARROW_SIZE) anchorLeftPosition = ARROW_SIZE;
  if (anchorLeftPosition > popoverRect.width - ARROW_SIZE)
    anchorLeftPosition = popoverRect.width - ARROW_SIZE;

  /*                               */
  return {
    anchorLeftPosition: showAnchor ? `${anchorLeftPosition}px` : false,
    position: finalPosition as PopoverPosition,
    popoverStyles: {
      /*                              */
      inset: "unset",
      margin: "unset",
      border: "unset",
      background: "unset",
      padding: "unset",
      /*         */
      position: useFixedPositioning ? "fixed" : "absolute",
      ...(top !== undefined && { top: `${top}px` }),
      ...(bottom !== undefined && { bottom: `${bottom}px` }),
      ...(left !== undefined && { left: `${left}px` }),
      ...(right !== undefined && { right: `${right}px` }),
      maxWidth: `min(var(--popover-max-width, ${DEFAULT_MAX_WIDTH}px), ${clippingRect.width - clippingDistance * 2}px)`, /*                  */
      maxHeight: `${clippingRect.height}px`,
    },
  };
};

export const obj2style = (obj: Record<string, string | number>): string =>
  Object.entries(obj)
    .map(([k, v]) => `${k.replace(/[A-Z]/g, (m) => `-${m.toLowerCase()}`)}:${v}`)
    .join(";");
